import axios from 'axios'
import * as Sentry from '@sentry/browser'
import { history } from '~/navigation/history'
import { ROOT_URL, ERRORS } from '../constants'
import { asyncLocalStorage } from '~/utils'
import { logger } from '~/helpers/logger'
import { showToast } from '~/components/toast'
import firebase from '~/services/firebase'
import { FEATURE_FLAGS, isFeatureEnabled } from '~/services/featureFlags'

export const api = axios.create({
  baseURL: ROOT_URL.guarantor
})

const getToken = () => {
  return asyncLocalStorage.getItem('token')
}

const transactionId = Math.random()
  .toString(36)
  .substr(2, 9)

api.interceptors.request.use(
  async config => {
    const token = await getToken()
    if (token) {
      Sentry.configureScope(function(scope) {
        scope.setTag('x-request-id', transactionId)
      })
      config.headers.authorization = token
      config.headers['x-request-id'] = transactionId
    }
    return config
  },
  error => {
    if (error.response.status === 403) {
      history.push('/token')
    } else {
      return Promise.reject(error)
    }
  }
)

const checkIfNotAuthorized = errorMessage => {
  if ([ERRORS.auth.invalidToken, ERRORS.auth.expiredToken, ERRORS.auth.notAuthorized].includes(errorMessage)) {
    localStorage.removeItem('token')
    history.push('/token')
  }
}

const treatError = (error, shouldCheckIfNotAuthorized = true) => {
  if (shouldCheckIfNotAuthorized) {
    const errorsArray = error?.response?.data?.errors
    if (['array', 'object'].includes(typeof errorsArray)) {
      for (const err in errorsArray) {
        checkIfNotAuthorized(err.message)
      }
    }
  }

  Sentry.addBreadcrumb({
    category: 'errorRequest',
    message: `ERROR: ${error}`,
    level: Sentry.Severity.Warning
  })

  throw error
}

// events

export const getAccessToken = token => {
  try {
    return api.get(`/public-token/${token}`)
  } catch (error) {
    treatError(error, false)
  }
}

export const authToken = (cpf, urlToken) => {
  try {
    return api.post('/public-token', {
      cpf,
      publicToken: urlToken
    })
  } catch (error) {
    treatError(error)
  }
}

export const getCreditRequestInfo = () => {
  try {
    return api.get('/credit-request-info')
  } catch (error) {
    treatError(error)
  }
}

export const getGuarantorInfo = screen => {
  try {
    return api.get('/guarantor-info')
  } catch (error) {
    logger({ error, screen })
    treatError(error)
  }
}

export const getPersonalDocument = (type, side) => {
  try {
    let urlParams
    if (side) {
      urlParams = `documentType=${type}&photoSide=${side}`
    } else {
      urlParams = `documentType=${type}`
    }
    return api.get(`/personal-document?${urlParams}`)
  } catch (error) {
    treatError(error)
  }
}

export const putGuarantorInfo = async data => {
  try {
    const { email, name, phone, number, complement, state, city, zipcode, district, street, birthDate } = data

    let addressObj = {}
    if (number && state && city && zipcode && district && street) {
      addressObj = {
        address: {
          number,
          complement,
          state,
          city,
          zipcode,
          district,
          street
        }
      }
    }

    return await api.put('/guarantor-info', {
      email,
      fullName: name,
      phone,
      ...addressObj,
      birthDate
    })
  } catch (error) {
    treatError(error)
  }
}

export const postSendEmail = ({ resend = false }) => {
  try {
    const endpoint = resend ? '/email/resend' : '/email/send'
    return api.post(endpoint)
  } catch (error) {
    error.response.status !== 403 && logger({ error, screen: '(fn postSendEmail)' })
    treatError(error)
  }
}

export const postSendPhone = () => {
  try {
    return api.post('/phone/send')
  } catch (error) {
    treatError(error)
  }
}

export const postVerifyPhone = data => {
  try {
    return api.post('/phone/verify', {
      data
    })
  } catch (error) {
    treatError(error)
  }
}

export const postPersonalDocument = data => {
  try {
    return api.post('/personal-document', data)
  } catch (error) {
    treatError(error)
  }
}

export const getProofOfAddress = () => {
  try {
    return api.get('/proof-of-address')
  } catch (error) {
    treatError(error)
  }
}

export const postProofOfAddress = data => {
  try {
    return api.post('/proof-of-address', {
      downloadURL: data
    })
  } catch (error) {
    logger({ error, screen: '/email' })
    treatError(error)
  }
}

export const getSelfie = () => {
  try {
    return api.get('/selfie')
  } catch (error) {
    treatError(error)
  }
}

export const postSelfie = data => {
  try {
    return api.post('/selfie', {
      downloadURL: data
    })
  } catch (error) {
    treatError(error)
  }
}

export const acceptedTermsAndConditions = async ip => {
  try {
    if (ip) {
      await api.put('/credit-request-info', {
        ip
      })
    } else {
      const { data } = await axios.get('https://api.ipify.org/?format=json')

      await api.put('/credit-request-info', {
        ip: data.ip
      })
    }
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'errorRequest',
      message: `ERROR: ${error}`,
      level: Sentry.Severity.Warning
    })
  }
}

export const DOCUMENT_TYPES = {
  selfie: 'selfie',
  personalDocument: 'personal-document'
}

/**
 * Sobe o arquivo direto no S3 com o presigned POST gerado pelo ms_guarantor.
 * Devolve a key, que vai no lugar da URL para /selfie e /personal-document.
 * @param {File|Blob} file
 * @param {string} documentType DOCUMENT_TYPES
 * @returns {Promise<{ key: string, typeExtension: string }>}
 */
export const uploadDocument = async (file, documentType) => {
  const { data: response } = await api.post('/document-upload', {
    documentType,
    contentType: file.type || 'application/octet-stream'
  })
  const { url, fields, key } = response.data

  // Os campos assinados vão antes do arquivo, como o S3 exige.
  const form = new FormData()
  Object.entries(fields).forEach(([name, value]) => form.append(name, value))
  form.append('file', file)

  await axios.post(url, form)

  return { key, typeExtension: fields['Content-Type'] }
}

/**
 * Upload legado no Firebase Storage, usado enquanto a flag do S3 privado está desligada.
 * @param {File|Blob} file
 * @param {string|null} asset
 * @returns {Promise<string>} URL pública do arquivo
 */
const uploadToFirebase = async (file, asset = null) => {
  const response = await getCreditRequestInfo()
  const { creditRequest } = response.data
  const crid = creditRequest.id ? creditRequest.id : '00000'
  const now = new Date().getTime()
  const storagePath = asset ? `/${crid}/${asset}/${now}` : `/${crid}/${now}`

  await firebase.auth().signInAnonymously()

  const uploadRef = firebase.storage().ref(storagePath)
  await uploadRef.put(file)

  return uploadRef.getDownloadURL()
}

/**
 * Sobe o documento no S3 privado ou, com a flag desligada, no Firebase.
 * Devolve o que vai para /selfie e /personal-document: { key, typeExtension }
 * no S3 ou { data } (URL) no Firebase.
 * @param {File|Blob} file
 * @param {string} documentType DOCUMENT_TYPES
 * @param {string|null} asset
 * @returns {Promise<{ key: string, typeExtension: string } | { data: string }>}
 */
export const storeDocument = async (file, documentType, asset = null) => {
  if (await isFeatureEnabled(FEATURE_FLAGS.privateS3Documents)) {
    return uploadDocument(file, documentType)
  }

  return { data: await uploadToFirebase(file, asset) }
}

export const uploadImages = async (location, array, asset = null) => {
  const result = []
  if (array.length > 0) {
    try {
      const documentType = location === 'selfie' ? DOCUMENT_TYPES.selfie : DOCUMENT_TYPES.personalDocument

      await Promise.all(
        array.map(async item => {
          const storedFile = await storeDocument(item, documentType)

          if (!storedFile.key && (!storedFile.data || typeof storedFile.data !== 'string')) {
            logger({ error: `Erro ao subir ${location} para o storage` })
            Sentry.captureException(new Error(`Erro ao subir ${location} para o storage`))

            throw new Error(`Erro ao subir ${location} para o storage`)
          }

          const uploaded = { typeExtension: item.type, ...storedFile }

          if (asset) {
            result.push({ ...uploaded, type: asset })
          } else {
            result.push(uploaded)
          }
        })
      )
    } catch (error) {
      showToast(error && error.message)
      Sentry.addBreadcrumb({
        category: 'errorRequest',
        message: `ERROR: ${error}`,
        level: Sentry.Severity.Warning
      })
    }
  }
  return result
}
